import { LOCALES, DEFAULT_LOCALE, routing, type AppLocale } from "./routing";

const NON_DEFAULT_LOCALES = LOCALES.filter((l) => l !== DEFAULT_LOCALE);

// Matches a leading non-default locale segment. The boundary `(?=[/?#]|$)`
// breaks on a slash, query, fragment, OR end-of-string — so "/it", "/it/x",
// "/it?q=1" and "/it#h" all match, while "/iterate" and "/items" do NOT.
const LEADING_LOCALE_RE = new RegExp(
  `^/(${NON_DEFAULT_LOCALES.join("|")})(?=[/?#]|$)`,
);

// True for absolute or protocol-relative URLs — these must pass through
// untouched (never get a locale prefix glued in front).
const ABSOLUTE_RE = /^(?:[a-z][a-z0-9+.-]*:)?\/\//i;

// This helper hand-derives the URL prefix and is therefore ONLY correct for
// the `as-needed` strategy (default locale unprefixed, others prefixed). If
// `routing.localePrefix` ever changes, the assertion below trips at module
// load (and localeHref.prefix-policy.test.ts fails) — a loud, deliberate
// signal to revisit this file rather than a silent split-brain with `routing`.
const PREFIX_MODE =
  typeof routing.localePrefix === "string"
    ? routing.localePrefix
    : routing.localePrefix?.mode;
if (PREFIX_MODE !== "as-needed") {
  throw new Error(
    `localeHref assumes localePrefix "as-needed" but routing uses "${PREFIX_MODE}". ` +
      `Update localeHref's prefix logic (or delegate to next-intl getPathname).`,
  );
}

/**
 * Read the active locale from a pathname (defaults to the current URL on
 * the client; falls back to EN on the server). EN is unprefixed, so any
 * non-prefixed path is EN.
 */
export function localeFromPath(pathname?: string): AppLocale {
  const p =
    pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "/");
  const m = p.match(LEADING_LOCALE_RE);
  return (m?.[1] as AppLocale) ?? DEFAULT_LOCALE;
}

/**
 * Strip a leading non-default locale prefix from a pathname, preserving any
 * query string and fragment. Use this BEFORE `split("/")`-style segment/slug
 * parsing so IT users don't get an off-by-one shift.
 *
 *   "/it/home/apple/overview" → "/home/apple/overview"
 *   "/it?x=1"                 → "/?x=1"
 *   "/it#h"                   → "/#h"
 *   "/iterate"                → "/iterate"  (not a locale; untouched)
 */
export function stripLocale(pathname: string): string {
  const stripped = pathname.replace(LEADING_LOCALE_RE, "");
  // After stripping "/it" from "/it?x=1" we get "?x=1"; re-root it to "/?x=1".
  if (stripped === "" || stripped.startsWith("?") || stripped.startsWith("#")) {
    return `/${stripped}`;
  }
  return stripped;
}

/**
 * Build a locale-aware absolute path for hard navigations
 * (`window.location.href = …`) and the locale-correct `redirect_to`/return
 * URLs handed to external services (Stripe/Google/Bing).
 *
 * EN-default paths come back unprefixed; under IT they get `/it` — so an IT
 * user is never dropped onto the EN tree. Query string and fragment are
 * preserved. Absolute/external URLs pass through unchanged. Idempotent:
 * an already-prefixed input is normalized, not double-prefixed.
 *
 *   localeHref("/auth/login")             // EN → "/auth/login"
 *   localeHref("/auth/login")             // IT → "/it/auth/login"
 *   localeHref("/auth/login", "it")       // → "/it/auth/login"
 *   localeHref("/home/x?tab=1", "it")     // → "/it/home/x?tab=1"
 *   localeHref("/it?x=1", "it")           // → "/it?x=1" (no double prefix)
 *   localeHref("https://stripe.com/x")    // → "https://stripe.com/x" (untouched)
 */
export function localeHref(path: string, locale?: AppLocale): string {
  // Absolute / protocol-relative URLs are not ours to prefix.
  if (ABSOLUTE_RE.test(path)) return path;

  const active = locale ?? localeFromPath();
  const clean = path.startsWith("/") ? path : `/${path}`;

  // Normalize away any existing locale prefix first (idempotency), keeping
  // the query/fragment intact via stripLocale.
  const bare = stripLocale(clean);
  if (active === DEFAULT_LOCALE) return bare;

  // Prefix with the active locale. A root path ("/", or "/?x=1" / "/#h")
  // must collapse its leading slash so we get "/it", "/it?x=1", "/it#h" —
  // not "/it/" or "/it/?x=1".
  if (bare === "/") return `/${active}`;
  if (bare.startsWith("/?") || bare.startsWith("/#")) {
    return `/${active}${bare.slice(1)}`;
  }
  return `/${active}${bare}`;
}
